import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatPercent, formatNumber, formatCurrency, getReturnColor } from '@/lib/utils'
import { MetricCard, LoadingSpinner } from '@/components/ui'
import { LineChart } from '@/components/charts'
import HeatmapTable from '@/components/charts/HeatmapTable'
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ZAxis, Legend, ReferenceLine,
} from 'recharts'
import { Play, Plus, Trash2, AlertCircle, Download, Pencil, Scale } from 'lucide-react'

// ── Types ──

interface FundEntry {
  fund_id: number
  name: string
  data_source: 'alternatives' | 'hfr'
  value: number   // dollar amount (value mode)
  weight: number  // percentage 0-100 (weight mode)
}

interface SimResult {
  portfolio_name: string
  start_date: string
  end_date: string
  total_return: number
  ann_return: number
  ann_volatility: number
  sharpe_ratio: number
  sortino_ratio: number
  max_drawdown: number
  nav_series: { date: string; nav: number; drawdown: number }[]
  holdings: { fund_id: number; name: string; weight: number; data_source: string }[]
}

type AnalysisTab = 'overview' | 'univariate' | 'weights' | 'correlations' | 'marginal' | 'skewsharpe' | 'uniqueness' | 'corrquadrant' | 'conditional'

const ANALYSIS_TABS: { key: AnalysisTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'univariate', label: 'Univariate' },
  { key: 'weights', label: 'Model Weights' },
  { key: 'correlations', label: 'Correlations' },
  { key: 'marginal', label: 'Marginal Impact' },
  { key: 'skewsharpe', label: 'Skew-Sharpe' },
  { key: 'uniqueness', label: 'Uniqueness' },
  { key: 'corrquadrant', label: 'Corr Quadrant' },
  { key: 'conditional', label: 'Conditional' },
]

const REBALANCE_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'none', label: 'No Rebalancing' },
]

const pct = (v: number | null | undefined) => v == null ? '-' : `${(v * 100).toFixed(2)}%`
const dec = (v: number | null | undefined, d = 2) => v == null ? '-' : v.toFixed(d)
const fmtImpact = (v: number | null | undefined) => {
  if (v == null) return '-'
  const s = (v * 100).toFixed(2)
  return v > 0 ? `+${s}%` : `${s}%`
}

// Data-freshness badge (reuses the Data Validation monthly rule: Fresh <=76d,
// Delayed <=152d, Stale beyond — computed server-side in /simulate/coverage).
function covStatus(s: string) {
  switch (s) {
    case 'fresh': return { cls: 'bg-green-500/10 text-green-700', dot: 'bg-green-500', label: 'Fresh' }
    case 'delayed': return { cls: 'bg-amber-500/10 text-amber-700', dot: 'bg-amber-500', label: 'Delayed' }
    case 'stale': return { cls: 'bg-red-600/10 text-red-700', dot: 'bg-red-600', label: 'Stale' }
    default: return { cls: 'bg-slate-400/10 text-slate-500', dot: 'bg-slate-300', label: 'No data' }
  }
}

export default function PortfolioSimulation() {
  // ── Construction state ──
  const [entries, setEntries] = useState<FundEntry[]>([])
  const [mode, setMode] = useState<'value' | 'weight'>('weight')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [rebalance, setRebalance] = useState('monthly')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showResults, setShowResults] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // ── Results state ──
  const [analysisTab, setAnalysisTab] = useState<AnalysisTab>('overview')
  const [condBench, setCondBench] = useState('SPTR')
  const [condMetric, setCondMetric] = useState('performance')

  // ── Debounced search ──
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // ── Fund search query ──
  const { data: searchResults, isFetching: searching } = useQuery({
    queryKey: ['fund-search', debouncedSearch],
    queryFn: () => api.get(`/demo-portfolios/search/funds?q=${encodeURIComponent(debouncedSearch)}&limit=30`).then(r => r.data),
    enabled: debouncedSearch.length >= 2,
  })

  // ── Data coverage (reactive on the selected funds) ──
  const coverageSig = useMemo(
    () => entries.map(e => `${e.fund_id}-${e.data_source}`).join(','),
    [entries]
  )
  const { data: coverage } = useQuery({
    queryKey: ['sim-coverage', coverageSig],
    queryFn: () => api.post('/portfolio/simulate/coverage', {
      funds: entries.map(e => ({ fund_id: e.fund_id, data_source: e.data_source })),
    }).then(r => r.data),
    enabled: entries.length >= 1,
  })

  // Filter out already-selected funds
  const filteredResults = useMemo(() => {
    if (!searchResults) return []
    const selected = new Set(entries.map(e => `${e.fund_id}-${e.data_source}`))
    return searchResults.filter((f: any) => !selected.has(`${f.fund_id}-${f.data_source}`))
  }, [searchResults, entries])

  // ── Load current portfolio ──
  const loadPortfolioMutation = useMutation({
    mutationFn: () => api.get('/portfolio').then(r => r.data),
    onSuccess: (data: any) => {
      if (data.holdings?.length) {
        setEntries(data.holdings.map((h: any) => ({
          fund_id: h.fund_id,
          name: h.fund_name,
          data_source: 'alternatives' as const,
          value: h.value || 0,
          weight: (h.weight || 0) * 100,
        })))
        setMode('value')
      }
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to load portfolio'),
  })

  // ── Simulation mutation ──
  const simMutation = useMutation({
    mutationFn: (payload: any) => api.post('/portfolio/simulate', payload).then(r => r.data),
    onSuccess: () => {
      setShowResults(true)
      setAnalysisTab('overview')
      // Trigger analysis
      analysisM.mutate({
        fund_ids: entries.map(e => e.fund_id),
        fund_names: entries.map(e => e.name),
        weights: normalizedWeights,
        data_sources: entries.map(e => e.data_source),
        months: 36,
      })
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Simulation failed'),
  })

  // ── Analysis mutation ──
  const analysisM = useMutation({
    mutationFn: (payload: any) => api.post('/portfolio/simulate/analyze', payload).then(r => r.data),
    onError: (err: any) => console.error('Analysis failed:', err),
  })

  // ── Computed values ──
  const totalValue = useMemo(() => entries.reduce((s, e) => s + e.value, 0), [entries])
  const totalWeight = useMemo(() => entries.reduce((s, e) => s + e.weight, 0), [entries])

  const normalizedWeights = useMemo(() => {
    if (mode === 'value') {
      if (totalValue <= 0) return entries.map(() => 0)
      return entries.map(e => e.value / totalValue)
    }
    return entries.map(e => e.weight / 100)
  }, [entries, mode, totalValue])

  const isValid = entries.length >= 1 && (
    mode === 'value'
      ? totalValue > 0
      : Math.abs(totalWeight - 100) < 0.5
  )

  // ── Actions ──
  const addFund = useCallback((fund: any) => {
    setEntries(prev => [...prev, {
      fund_id: fund.fund_id,
      name: fund.fund_name,
      data_source: fund.data_source,
      value: 0,
      weight: 0,
    }])
    setSearchTerm('')
    setShowDropdown(false)
  }, [])

  const removeFund = useCallback((fundId: number, source: string) => {
    setEntries(prev => prev.filter(e => !(e.fund_id === fundId && e.data_source === source)))
  }, [])

  const updateValue = useCallback((fundId: number, source: string, val: number) => {
    setEntries(prev => prev.map(e =>
      e.fund_id === fundId && e.data_source === source ? { ...e, value: val } : e
    ))
  }, [])

  const updateWeight = useCallback((fundId: number, source: string, w: number) => {
    setEntries(prev => prev.map(e =>
      e.fund_id === fundId && e.data_source === source ? { ...e, weight: w } : e
    ))
  }, [])

  const equalWeight = useCallback(() => {
    if (entries.length === 0) return
    const w = Math.round((100 / entries.length) * 100) / 100
    setEntries(prev => prev.map((e, i) => ({
      ...e,
      weight: i === 0 ? 100 - w * (prev.length - 1) : w,
    })))
  }, [entries.length])

  const handleSimulate = () => {
    if (!isValid) return
    setError(null)
    simMutation.mutate({
      fund_ids: entries.map(e => e.fund_id),
      weights: normalizedWeights,
      data_sources: entries.map(e => e.data_source),
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      rebalance_frequency: rebalance,
    })
  }

  const result: SimResult | undefined = simMutation.data
  const analysis: any = analysisM.data

  // ── Filtered conditional ──
  const filteredCond = useMemo(() => {
    if (!analysis?.conditional) return []
    return analysis.conditional.filter((r: any) => r.benchmark === condBench && r.metric === condMetric)
  }, [analysis, condBench, condMetric])

  const condBenchmarks = useMemo(() => {
    if (!analysis?.conditional) return []
    return [...new Set(analysis.conditional.map((r: any) => r.benchmark))] as string[]
  }, [analysis])

  const condMetrics = useMemo(() => {
    if (!analysis?.conditional) return []
    return [...new Set(analysis.conditional.map((r: any) => r.metric))] as string[]
  }, [analysis])

  // Scatter data for skew-sharpe
  const scatterData = useMemo(() => {
    if (!analysis?.skew_sharpe?.points) return { funds: [], portfolio: [] }
    return {
      funds: analysis.skew_sharpe.points.filter((p: any) => p.category === 'fund'),
      portfolio: analysis.skew_sharpe.points.filter((p: any) => p.category === 'portfolio'),
    }
  }, [analysis])

  // ══════════════════════════════════════════════════════════════════════════
  // ── CONSTRUCTION PHASE ──
  // ══════════════════════════════════════════════════════════════════════════

  if (!showResults) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Portfolio Simulation</h1>
          <p className="text-muted-foreground">Build a custom portfolio and backtest allocations</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Portfolio Construction */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Portfolio Construction</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadPortfolioMutation.mutate()}
                disabled={loadPortfolioMutation.isPending}
                className="text-xs px-3 py-1.5 border border-input rounded-md hover:bg-accent flex items-center gap-1.5"
              >
                <Download className="h-3 w-3" />
                {loadPortfolioMutation.isPending ? 'Loading...' : 'Load Portfolio'}
              </button>
              {entries.length > 0 && (
                <button
                  onClick={equalWeight}
                  className="text-xs px-3 py-1.5 border border-input rounded-md hover:bg-accent flex items-center gap-1.5"
                >
                  <Scale className="h-3 w-3" />
                  Equal Weight
                </button>
              )}
              {/* Mode toggle */}
              <div className="flex border border-input rounded-md overflow-hidden text-xs">
                <button
                  onClick={() => setMode('weight')}
                  className={`px-3 py-1.5 ${mode === 'weight' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
                >
                  Weight %
                </button>
                <button
                  onClick={() => setMode('value')}
                  className={`px-3 py-1.5 ${mode === 'value' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
                >
                  Value $
                </button>
              </div>
            </div>
          </div>

          {/* Fund search */}
          <div className="relative mb-4" ref={dropdownRef}>
            <input
              type="text"
              placeholder="Search funds across Alternatives and HFR..."
              className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setShowDropdown(true)
              }}
              onFocus={() => setShowDropdown(true)}
            />
            {searching && (
              <div className="absolute right-3 top-3">
                <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {showDropdown && debouncedSearch.length >= 2 && filteredResults.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredResults.map((f: any) => (
                  <button
                    key={`${f.fund_id}-${f.data_source}`}
                    onClick={() => addFund(f)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center gap-2"
                  >
                    <Plus className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="truncate flex-1">{f.fund_name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                      f.data_source === 'hfr'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {f.data_source === 'hfr' ? 'HFR' : 'ALT'}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {showDropdown && debouncedSearch.length >= 2 && !searching && filteredResults.length === 0 && (
              <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-md shadow-lg p-3 text-sm text-muted-foreground">
                No funds found
              </div>
            )}
          </div>

          {/* Portfolio table */}
          {entries.length === 0 ? (
            <div className="text-center text-muted-foreground py-8 text-sm">
              Search and add funds above, or load your current portfolio
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-1 mb-1">
                <div className="col-span-1">Source</div>
                <div className="col-span-5">Fund</div>
                <div className="col-span-2 text-right">{mode === 'value' ? 'Value ($)' : 'Weight (%)'}</div>
                <div className="col-span-2 text-right">Weight</div>
                <div className="col-span-2" />
              </div>
              {entries.map((entry) => {
                const computedWeight = mode === 'value'
                  ? (totalValue > 0 ? (entry.value / totalValue * 100) : 0)
                  : entry.weight
                return (
                  <div key={`${entry.fund_id}-${entry.data_source}`} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        entry.data_source === 'hfr'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {entry.data_source === 'hfr' ? 'HFR' : 'ALT'}
                      </span>
                    </div>
                    <div className="col-span-5 text-sm truncate" title={entry.name}>
                      {entry.name}
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        step={mode === 'value' ? '1000' : '0.1'}
                        min="0"
                        className="w-full h-8 px-2 border border-input rounded-md bg-background text-sm text-right"
                        value={mode === 'value' ? (entry.value || '') : (entry.weight || '')}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0
                          if (mode === 'value') {
                            updateValue(entry.fund_id, entry.data_source, val)
                          } else {
                            updateWeight(entry.fund_id, entry.data_source, val)
                          }
                        }}
                      />
                    </div>
                    <div className="col-span-2 text-right text-sm font-mono text-muted-foreground">
                      {computedWeight.toFixed(1)}%
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <button
                        onClick={() => removeFund(entry.fund_id, entry.data_source)}
                        className="p-1.5 text-muted-foreground hover:text-red-600 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )
              })}
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-sm font-medium">
                  {mode === 'value' ? (
                    <>Total: <span className={totalValue > 0 ? 'text-green-600' : 'text-muted-foreground'}>{formatCurrency(totalValue)}</span></>
                  ) : (
                    <>Total: <span className={Math.abs(totalWeight - 100) < 0.5 ? 'text-green-600' : 'text-red-600'}>{formatNumber(totalWeight, 1)}%</span></>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">{entries.length} fund{entries.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          )}
        </div>

        {/* Data Coverage */}
        {entries.length >= 1 && coverage && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="text-lg font-semibold">Data Coverage</h2>
              {coverage.common_window ? (
                <div className="text-sm">
                  <span className="text-muted-foreground">Common effective window: </span>
                  <span className="font-semibold">{coverage.common_window.start} → {coverage.common_window.end}</span>
                  <span className="text-muted-foreground"> ({coverage.common_window.months} mo)</span>
                </div>
              ) : (
                <span className="text-sm text-red-600 font-medium">No common overlap between selected funds</span>
              )}
            </div>

            {(coverage.limiting_end?.length > 0 || coverage.limiting_start?.length > 0) && (
              <div className="text-xs text-amber-800 bg-amber-500/10 border border-amber-200 rounded px-3 py-2 mb-3 flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div className="space-y-0.5">
                  {coverage.limiting_end?.length > 0 && (
                    <div>Window <b>end</b> limited by <b>{coverage.limiting_end.join(', ')}</b> (earliest last return)</div>
                  )}
                  {coverage.limiting_start?.length > 0 && (
                    <div>Window <b>start</b> limited by <b>{coverage.limiting_start.join(', ')}</b> (latest first return)</div>
                  )}
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="data-table text-sm">
                <thead>
                  <tr>
                    <th>Fund</th>
                    <th>Start Date</th>
                    <th>Last Return</th>
                    <th className="text-right"># Obs</th>
                    <th>Data Status</th>
                  </tr>
                </thead>
                <tbody>
                  {coverage.funds.map((f: any) => {
                    const st = covStatus(f.status)
                    const isEndLimiter = coverage.limiting_end?.includes(f.name)
                    return (
                      <tr key={`${f.fund_id}-${f.data_source}`}>
                        <td className="font-medium">{f.name}</td>
                        <td className="font-mono text-xs">{f.start_date || '—'}</td>
                        <td className={`font-mono text-xs ${isEndLimiter ? 'text-amber-700 font-semibold' : ''}`}>
                          {f.last_date || '—'}
                        </td>
                        <td className="text-right font-mono text-xs">{f.n_obs}</td>
                        <td>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              The backtest runs on the <b>common effective window</b> (months where every fund has data). A late start
              is fine; a <b>Stale</b> last return means that fund truncates the window — swap it out to extend the backtest.
            </p>
          </div>
        )}

        {/* Parameters & Run */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Simulation Parameters</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Start Date</label>
              <input
                type="date"
                className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">End Date</label>
              <input
                type="date"
                className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Rebalance</label>
              <select
                className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                value={rebalance}
                onChange={(e) => setRebalance(e.target.value)}
              >
                {REBALANCE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleSimulate}
              disabled={!isValid || simMutation.isPending}
              className="h-10 px-6 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Play className="h-4 w-4" />
              {simMutation.isPending ? 'Running...' : 'Simulate'}
            </button>
          </div>
          {!isValid && entries.length > 0 && mode === 'weight' && (
            <p className="text-xs text-red-500 mt-2">Weights must sum to 100% (currently {formatNumber(totalWeight, 1)}%)</p>
          )}
        </div>

        {simMutation.isPending && <LoadingSpinner message="Running simulation..." />}
      </div>
    )
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ── RESULTS PHASE ──
  // ══════════════════════════════════════════════════════════════════════════

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Simulation Results</h1>
          <p className="text-muted-foreground">
            Effective window: <span className="font-medium text-foreground">{result?.start_date} → {result?.end_date}</span>
            {' '}&middot; {entries.length} funds
            {coverage?.limiting_end?.length > 0 && (
              <span className="text-amber-700"> &middot; window end limited by {coverage.limiting_end.join(', ')}</span>
            )}
          </p>
        </div>
        <button
          onClick={() => setShowResults(false)}
          className="px-4 py-2 text-sm border border-input rounded-md hover:bg-accent flex items-center gap-2"
        >
          <Pencil className="h-4 w-4" />
          Edit Portfolio
        </button>
      </div>

      {/* Metric cards */}
      {result && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <MetricCard label="Total Return" value={formatPercent(result.total_return)} />
          <MetricCard label="Ann. Return" value={formatPercent(result.ann_return)} />
          <MetricCard label="Ann. Volatility" value={formatPercent(result.ann_volatility)} />
          <MetricCard label="Sharpe Ratio" value={formatNumber(result.sharpe_ratio)} />
          <MetricCard label="Sortino Ratio" value={formatNumber(result.sortino_ratio)} />
          <MetricCard label="Max Drawdown" value={formatPercent(result.max_drawdown)} />
        </div>
      )}

      {/* Analysis tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {ANALYSIS_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setAnalysisTab(t.key)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              analysisTab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {analysisM.isPending && <LoadingSpinner message="Computing analysis..." />}

      {/* ── OVERVIEW TAB ── */}
      {analysisTab === 'overview' && result && (
        <div className="space-y-6">
          {/* NAV Chart */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">NAV Series</h2>
            <LineChart
              data={result.nav_series}
              xKey="date"
              lines={[{ key: 'nav', name: 'NAV', color: 'hsl(var(--primary))' }]}
              height={350}
              yTickFormatter={(v: number) => formatNumber(v, 3)}
            />
          </div>

          {/* Drawdown Chart */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Drawdown</h2>
            <LineChart
              data={result.nav_series}
              xKey="date"
              lines={[{ key: 'drawdown', name: 'Drawdown', color: 'hsl(0, 70%, 50%)' }]}
              height={250}
              yTickFormatter={(v: number) => formatPercent(v)}
            />
          </div>

          {/* Holdings table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border"><h2 className="font-semibold">Holdings</h2></div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Fund</th>
                  <th className="text-right">Weight</th>
                </tr>
              </thead>
              <tbody>
                {result.holdings.map(h => (
                  <tr key={`${h.fund_id}-${h.data_source}`}>
                    <td>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        h.data_source === 'hfr' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {h.data_source === 'hfr' ? 'HFR' : 'ALT'}
                      </span>
                    </td>
                    <td>{h.name}</td>
                    <td className="text-right font-mono">{formatPercent(h.weight)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── UNIVARIATE TAB ── */}
      {analysisTab === 'univariate' && analysis?.univariate && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border"><h2 className="font-semibold">Univariate Analysis</h2></div>
          <div className="overflow-x-auto">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-card z-10">Fund</th>
                  <th className="text-right">Return</th>
                  <th className="text-right">Vol</th>
                  <th className="text-right">Sharpe</th>
                  <th className="text-right">Sortino</th>
                  <th className="text-right">Max DD</th>
                  <th className="text-right">Cur DD</th>
                  <th className="text-right">Recovery</th>
                  <th className="text-right">Skew</th>
                  <th className="text-right">Kurt</th>
                  <th className="text-right">VaR 95</th>
                  <th className="text-right">CVaR 95</th>
                  <th className="text-right">Win%</th>
                  <th className="text-right">Best Mo</th>
                  <th className="text-right">Worst Mo</th>
                  <th className="text-right">Months</th>
                </tr>
              </thead>
              <tbody>
                {analysis.univariate.map((u: any) => (
                  <tr key={u.name} className={u.name === 'Portfolio' ? 'font-semibold bg-muted/50' : ''}>
                    <td className="font-medium sticky left-0 bg-card z-10 whitespace-nowrap">{u.name}</td>
                    <td className={`text-right font-mono ${getReturnColor(u.ann_return)}`}>{pct(u.ann_return)}</td>
                    <td className="text-right font-mono">{pct(u.ann_volatility)}</td>
                    <td className="text-right font-mono">{dec(u.sharpe_ratio)}</td>
                    <td className="text-right font-mono">{dec(u.sortino_ratio)}</td>
                    <td className="text-right font-mono text-red-600">{pct(u.max_drawdown)}</td>
                    <td className="text-right font-mono text-red-600">{pct(u.current_drawdown)}</td>
                    <td className="text-right font-mono">{u.recovery_days != null ? `${u.recovery_days}d` : '-'}</td>
                    <td className="text-right font-mono">{dec(u.skewness)}</td>
                    <td className="text-right font-mono">{dec(u.kurtosis)}</td>
                    <td className="text-right font-mono text-red-600">{pct(u.var_95)}</td>
                    <td className="text-right font-mono text-red-600">{pct(u.cvar_95)}</td>
                    <td className="text-right font-mono">{pct(u.win_rate)}</td>
                    <td className="text-right font-mono text-green-600">{pct(u.best_month)}</td>
                    <td className="text-right font-mono text-red-600">{pct(u.worst_month)}</td>
                    <td className="text-right font-mono text-muted-foreground">{u.num_months || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── MODEL WEIGHTS TAB ── */}
      {analysisTab === 'weights' && analysis?.weight_comparison && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border"><h2 className="font-semibold">Actual vs Model Weights</h2></div>
          <div className="overflow-x-auto">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-card z-10">Fund</th>
                  <th className="text-right">Current</th>
                  <th className="text-right">ERC</th>
                  <th className="text-right">Inv Vol</th>
                  <th className="text-right">Max Div</th>
                  <th className="text-right">Max Sharpe</th>
                  <th className="text-right">Avg Model</th>
                  <th className="text-right">Diff</th>
                </tr>
              </thead>
              <tbody>
                {analysis.weight_comparison.map((w: any) => (
                  <tr key={w.fund_name}>
                    <td className="font-medium sticky left-0 bg-card z-10 whitespace-nowrap">{w.fund_name}</td>
                    <td className="text-right font-mono font-semibold">{pct(w.actual_weight)}</td>
                    <td className="text-right font-mono">{pct(w.erc_weight)}</td>
                    <td className="text-right font-mono">{pct(w.inv_vol_weight)}</td>
                    <td className="text-right font-mono">{pct(w.max_div_weight)}</td>
                    <td className="text-right font-mono">{pct(w.max_sharpe_weight)}</td>
                    <td className="text-right font-mono font-semibold">{pct(w.avg_model_weight)}</td>
                    <td className={`text-right font-mono ${w.difference > 0 ? 'text-green-600' : w.difference < 0 ? 'text-red-600' : ''}`}>
                      {fmtImpact(w.difference)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── CORRELATIONS TAB ── */}
      {analysisTab === 'correlations' && analysis?.correlations && (
        <div className="bg-card border border-border rounded-lg overflow-hidden p-4">
          <h2 className="font-semibold mb-4">Correlations (Full Sample)</h2>
          {analysis.correlations.funds?.length > 0 ? (
            <HeatmapTable
              data={analysis.correlations.matrix}
              rowLabels={analysis.correlations.funds}
              colLabels={analysis.correlations.funds}
              formatter={(v: number) => v.toFixed(2)}
              colorScale="diverging"
              minValue={-1}
              maxValue={1}
            />
          ) : (
            <div className="text-center text-muted-foreground p-8">No correlation data</div>
          )}
        </div>
      )}

      {/* ── MARGINAL IMPACT TAB ── */}
      {analysisTab === 'marginal' && analysis?.marginal_impact && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="font-semibold">Marginal Diversification Impact</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Impact on portfolio metrics when each fund is excluded (positive = fund helps)
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-card z-10">Fund</th>
                  <th className="text-right">Perf Impact</th>
                  <th className="text-right">Vol Impact</th>
                  <th className="text-right">Max DD Impact</th>
                  <th className="text-right">Sharpe Impact</th>
                  <th className="text-right">Recovery &Delta;</th>
                  <th className="text-right">Rcvry (excl)</th>
                </tr>
              </thead>
              <tbody>
                {analysis.marginal_impact.map((m: any) => (
                  <tr key={m.fund_name}>
                    <td className="font-medium sticky left-0 bg-card z-10 whitespace-nowrap">{m.fund_name}</td>
                    <td className={`text-right font-mono ${m.perf_impact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmtImpact(m.perf_impact)}
                    </td>
                    <td className={`text-right font-mono ${m.vol_impact < 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmtImpact(m.vol_impact)}
                    </td>
                    <td className={`text-right font-mono ${m.max_dd_impact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmtImpact(m.max_dd_impact)}
                    </td>
                    <td className={`text-right font-mono ${m.sharpe_impact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {m.sharpe_impact != null ? (m.sharpe_impact > 0 ? '+' : '') + m.sharpe_impact.toFixed(3) : '-'}
                    </td>
                    <td className="text-right font-mono">
                      {m.recovery_days_change != null ? `${m.recovery_days_change > 0 ? '+' : ''}${Math.round(m.recovery_days_change)}d` : '-'}
                    </td>
                    <td className="text-right font-mono text-muted-foreground">
                      {m.recovery_days_excl != null ? `${Math.round(m.recovery_days_excl)}d` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── SKEW-SHARPE TAB ── */}
      {analysisTab === 'skewsharpe' && (
        <div className="space-y-4">
          {scatterData.funds.length > 0 ? (
            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="font-semibold mb-2">Sharpe Ratio vs Skewness</h2>
              <p className="text-xs text-muted-foreground mb-4">Bubble size = Kurtosis. Diamond = Portfolio.</p>
              <ResponsiveContainer width="100%" height={500}>
                <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis
                    type="number" dataKey="skewness" name="Skewness"
                    tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                    label={{ value: 'Skewness', position: 'bottom', offset: 0, fontSize: 12 }}
                  />
                  <YAxis
                    type="number" dataKey="sharpe_ratio" name="Sharpe"
                    tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                    label={{ value: 'Sharpe Ratio', angle: -90, position: 'insideLeft', fontSize: 12 }}
                  />
                  <ZAxis type="number" dataKey="kurtosis" range={[80, 600]} name="Kurtosis" />
                  <Tooltip
                    content={({ payload }) => {
                      if (!payload?.length) return null
                      const d = payload[0].payload
                      return (
                        <div className="bg-card border border-border rounded-lg p-2 text-xs shadow-lg">
                          <div className="font-semibold">{d.name}</div>
                          <div>Skewness: {d.skewness?.toFixed(3)}</div>
                          <div>Sharpe: {d.sharpe_ratio?.toFixed(3)}</div>
                          <div>Kurtosis: {d.kurtosis?.toFixed(3)}</div>
                        </div>
                      )
                    }}
                  />
                  <Legend />
                  <ReferenceLine y={0} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" />
                  <ReferenceLine x={0} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" />
                  <Scatter name="Funds" data={scatterData.funds} fill="hsl(217, 91%, 50%)" opacity={0.7} />
                  <Scatter name="Portfolio" data={scatterData.portfolio} fill="hsl(38, 92%, 50%)" shape="diamond" opacity={0.9} />
                </ScatterChart>
              </ResponsiveContainer>
              {analysis?.skew_sharpe?.regression_slope != null && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Regression: y = {analysis.skew_sharpe.regression_slope.toFixed(3)}x + {analysis.skew_sharpe.regression_intercept.toFixed(3)}
                </p>
              )}
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">No skew-sharpe data available</div>
          )}
        </div>
      )}

      {/* ── UNIQUENESS TAB ── */}
      {analysisTab === 'uniqueness' && analysis?.uniqueness?.points?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="font-semibold mb-2">PCA Uniqueness vs Sharpe Ratio</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Bubble size = Volatility. High uniqueness + high Sharpe = best diversifiers.
          </p>
          <ResponsiveContainer width="100%" height={500}>
            <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
              <XAxis
                type="number" dataKey="uniqueness" name="Uniqueness"
                tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                label={{ value: 'Uniqueness (1 - R²)', position: 'bottom', offset: 0, fontSize: 12 }}
                domain={[0, 'auto']}
              />
              <YAxis
                type="number" dataKey="sharpe_ratio" name="Sharpe"
                tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                label={{ value: 'Sharpe Ratio', angle: -90, position: 'insideLeft', fontSize: 12 }}
              />
              <ZAxis type="number" dataKey="volatility" range={[80, 600]} name="Volatility" />
              <Tooltip
                content={({ payload }) => {
                  if (!payload?.length) return null
                  const d = payload[0].payload
                  return (
                    <div className="bg-card border border-border rounded-lg p-2 text-xs shadow-lg">
                      <div className="font-semibold">{d.name}</div>
                      <div>Uniqueness: {(d.uniqueness * 100).toFixed(1)}%</div>
                      <div>Sharpe: {d.sharpe_ratio?.toFixed(3)}</div>
                      <div>Volatility: {(d.volatility * 100).toFixed(2)}%</div>
                      <div className="text-muted-foreground">{d.num_pcs} PCs</div>
                    </div>
                  )
                }}
              />
              <Legend />
              {analysis.uniqueness.median_uniqueness != null && (
                <ReferenceLine x={analysis.uniqueness.median_uniqueness} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" label="Median" />
              )}
              {analysis.uniqueness.median_sharpe != null && (
                <ReferenceLine y={analysis.uniqueness.median_sharpe} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" />
              )}
              <Scatter name="Funds" data={analysis.uniqueness.points} fill="hsl(217, 91%, 50%)" opacity={0.7} />
            </ScatterChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 text-xs">
            <div className="p-2 bg-green-500/10 rounded text-center">
              <div className="font-semibold text-green-700">Stars</div>
              <div className="text-muted-foreground">High Sharpe + High Uniqueness</div>
            </div>
            <div className="p-2 bg-yellow-500/10 rounded text-center">
              <div className="font-semibold text-yellow-700">Redundant Alpha</div>
              <div className="text-muted-foreground">High Sharpe + Low Uniqueness</div>
            </div>
            <div className="p-2 bg-blue-500/10 rounded text-center">
              <div className="font-semibold text-blue-700">Unique but Weak</div>
              <div className="text-muted-foreground">Low Sharpe + High Uniqueness</div>
            </div>
            <div className="p-2 bg-red-500/10 rounded text-center">
              <div className="font-semibold text-red-700">Cut Candidates</div>
              <div className="text-muted-foreground">Low Sharpe + Low Uniqueness</div>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th>Fund</th>
                  <th className="text-right">Uniqueness</th>
                  <th className="text-right">Sharpe</th>
                  <th className="text-right">Volatility</th>
                  <th className="text-right"># PCs</th>
                </tr>
              </thead>
              <tbody>
                {[...analysis.uniqueness.points].sort((a: any, b: any) => b.uniqueness - a.uniqueness).map((p: any) => (
                  <tr key={p.name}>
                    <td className="font-medium">{p.name}</td>
                    <td className="text-right font-mono">{(p.uniqueness * 100).toFixed(1)}%</td>
                    <td className="text-right font-mono">{p.sharpe_ratio.toFixed(3)}</td>
                    <td className="text-right font-mono">{(p.volatility * 100).toFixed(2)}%</td>
                    <td className="text-right font-mono text-muted-foreground">{p.num_pcs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── CORRELATION QUADRANT TAB ── */}
      {analysisTab === 'corrquadrant' && analysis?.correlation_quadrant?.points?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="font-semibold mb-2">Fund-Portfolio Correlation vs Sharpe Ratio</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Correlation with portfolio (excl. fund) vs Sharpe. Bubble size = |Skewness|. Best funds: high Sharpe, low correlation.
          </p>
          <ResponsiveContainer width="100%" height={500}>
            <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
              <XAxis
                type="number" dataKey="correlation" name="Correlation"
                tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                label={{ value: 'Correlation with Portfolio (excl)', position: 'bottom', offset: 0, fontSize: 12 }}
                domain={[-1, 1]}
              />
              <YAxis
                type="number" dataKey="sharpe_ratio" name="Sharpe"
                tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                label={{ value: 'Sharpe Ratio', angle: -90, position: 'insideLeft', fontSize: 12 }}
              />
              <ZAxis type="number" dataKey="skewness" range={[80, 600]} name="Skewness" />
              <Tooltip
                content={({ payload }) => {
                  if (!payload?.length) return null
                  const d = payload[0].payload
                  return (
                    <div className="bg-card border border-border rounded-lg p-2 text-xs shadow-lg">
                      <div className="font-semibold">{d.name}</div>
                      <div>Correlation: {d.correlation?.toFixed(3)}</div>
                      <div>Sharpe: {d.sharpe_ratio?.toFixed(3)}</div>
                      <div>Skewness: {d.skewness?.toFixed(3)}</div>
                    </div>
                  )
                }}
              />
              <Legend />
              <ReferenceLine x={0} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" />
              <ReferenceLine y={0} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" />
              {analysis.correlation_quadrant.portfolio_sharpe != null && (
                <ReferenceLine y={analysis.correlation_quadrant.portfolio_sharpe} stroke="hsl(38, 92%, 50%)" strokeDasharray="5 5" label="Portfolio SR" />
              )}
              <Scatter name="Funds" data={analysis.correlation_quadrant.points} fill="hsl(217, 91%, 50%)" opacity={0.7} />
            </ScatterChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 text-xs">
            <div className="p-2 bg-green-500/10 rounded text-center">
              <div className="font-semibold text-green-700">Best Diversifiers</div>
              <div className="text-muted-foreground">High Sharpe + Low Correlation</div>
            </div>
            <div className="p-2 bg-yellow-500/10 rounded text-center">
              <div className="font-semibold text-yellow-700">Redundant Alpha</div>
              <div className="text-muted-foreground">High Sharpe + High Correlation</div>
            </div>
            <div className="p-2 bg-blue-500/10 rounded text-center">
              <div className="font-semibold text-blue-700">Diversifying but Weak</div>
              <div className="text-muted-foreground">Low Sharpe + Low Correlation</div>
            </div>
            <div className="p-2 bg-red-500/10 rounded text-center">
              <div className="font-semibold text-red-700">Cut Candidates</div>
              <div className="text-muted-foreground">Low Sharpe + High Correlation</div>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th>Fund</th>
                  <th className="text-right">Correlation</th>
                  <th className="text-right">Sharpe</th>
                  <th className="text-right">Skewness</th>
                </tr>
              </thead>
              <tbody>
                {[...analysis.correlation_quadrant.points].sort((a: any, b: any) => a.correlation - b.correlation).map((p: any) => (
                  <tr key={p.name}>
                    <td className="font-medium">{p.name}</td>
                    <td className="text-right font-mono">{p.correlation.toFixed(3)}</td>
                    <td className="text-right font-mono">{p.sharpe_ratio.toFixed(3)}</td>
                    <td className="text-right font-mono">{p.skewness.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── CONDITIONAL TAB ── */}
      {analysisTab === 'conditional' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">Benchmark:</label>
              {condBenchmarks.map((b: string) => (
                <button
                  key={b}
                  onClick={() => setCondBench(b)}
                  className={`px-3 py-1.5 text-xs rounded-md border ${
                    condBench === b
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-input hover:bg-accent'
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">Metric:</label>
              {condMetrics.map((m: string) => (
                <button
                  key={m}
                  onClick={() => setCondMetric(m)}
                  className={`px-3 py-1.5 text-xs rounded-md border capitalize ${
                    condMetric === m
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-input hover:bg-accent'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          {filteredCond.length > 0 ? (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border">
                <h2 className="font-semibold">Conditional {condMetric} — {condBench}</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Fund {condMetric} conditioned on {condBench} market regimes
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th className="sticky left-0 bg-card z-10">Fund</th>
                      <th className="text-right">Unconditional</th>
                      <th className="text-right">Lower Q</th>
                      <th className="text-right">Negative</th>
                      <th className="text-right">Positive</th>
                      <th className="text-right">Upper Q</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCond.map((r: any, i: number) => {
                      const fmt = condMetric === 'performance' ? pct : (v: number | null | undefined) => dec(v, 3)
                      return (
                        <tr key={i}>
                          <td className="font-medium sticky left-0 bg-card z-10 whitespace-nowrap">{r.fund}</td>
                          <td className="text-right font-mono">{fmt(r.unconditional)}</td>
                          <td className={`text-right font-mono ${r.conditional_lower_quartile < 0 ? 'text-red-600' : ''}`}>
                            {fmt(r.conditional_lower_quartile)}
                          </td>
                          <td className={`text-right font-mono ${r.conditional_negative < 0 ? 'text-red-600' : ''}`}>
                            {fmt(r.conditional_negative)}
                          </td>
                          <td className={`text-right font-mono ${r.conditional_positive > 0 ? 'text-green-600' : ''}`}>
                            {fmt(r.conditional_positive)}
                          </td>
                          <td className={`text-right font-mono ${r.conditional_upper_quartile > 0 ? 'text-green-600' : ''}`}>
                            {fmt(r.conditional_upper_quartile)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">
              {analysis?.conditional?.length ? 'No data for selected filters' : 'No conditional data available'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
